/**
 * The job checklist's editor and readout, the pure half (2026-09-16).
 *
 * The operator names steps in words ("Fibre terminated and cleaned"); the
 * platform keys them (`fibre_terminated`) so a job's ticks survive a
 * relabel. This module is where a label becomes a key, where a draft list
 * is judged before it is sent, and how a recorded value is printed — kept
 * out of the components so a test can pin each without a DOM.
 */
import {
  FIELD_CHECKLIST_KEY_PATTERN,
  FIELD_CHECKLIST_MAX_STEPS,
  type FieldChecklistEntry,
  type FieldChecklistKind,
  type FieldChecklistStep,
} from '@simpleacs/shared';

/** What each kind asks of the technician, for the editor's picker and the card's pill. */
export const CHECKLIST_KIND_LABELS: Record<FieldChecklistKind, string> = {
  tick: 'Tick',
  light_reading: 'Light reading (dBm)',
  text: 'Note',
};

/**
 * A key from a label: lower-case, letters and digits, runs of anything else
 * as one underscore, at most 40 characters — and unique among `taken`, with
 * a numeric suffix when the same words come round twice ("Customer shown"
 * and "Customer shown" → `customer_shown`, `customer_shown_2`). A label
 * with nothing usable in it becomes `step`.
 */
export function stepKeyFrom(label: string, taken: ReadonlySet<string> = new Set()): string {
  const base =
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 36) || 'step';
  const trimmed = base.replace(/_+$/g, '') || 'step';
  if (!taken.has(trimmed)) return trimmed;
  for (let n = 2; ; n += 1) {
    const candidate = `${trimmed}_${n}`;
    if (!taken.has(candidate)) return candidate;
  }
}

/** One row of the editor: a step, plus whether the key was minted here (so a relabel re-keys it) or came from the platform. */
export interface ChecklistDraftRow extends FieldChecklistStep {
  /** True for a row the operator added in this session — its key follows its label until it is saved. */
  fresh: boolean;
}

/** The editor's rows from the platform's list. */
export function draftRowsFrom(steps: readonly FieldChecklistStep[]): ChecklistDraftRow[] {
  return steps.map((s) => ({ ...s, fresh: false }));
}

/** A new, empty row at the end, keyed once it has a label. */
export function addDraftRow(rows: readonly ChecklistDraftRow[]): ChecklistDraftRow[] {
  return [...rows, { key: '', label: '', kind: 'tick', required: false, fresh: true }];
}

/**
 * Relabel one row. A FRESH row's key follows its label (unique against the
 * others); a saved row keeps its key, because the key is what the ticks
 * already recorded on open jobs hang off.
 */
export function relabelDraftRow(
  rows: readonly ChecklistDraftRow[],
  index: number,
  label: string,
): ChecklistDraftRow[] {
  return rows.map((row, i) => {
    if (i !== index) return row;
    if (!row.fresh) return { ...row, label };
    const taken = new Set(rows.filter((_, j) => j !== index).map((r) => r.key));
    return { ...row, label, key: label.trim() ? stepKeyFrom(label, taken) : '' };
  });
}

/** The list as it will be sent: trimmed labels, the rows in order. */
export function stepsFromDraft(rows: readonly ChecklistDraftRow[]): FieldChecklistStep[] {
  return rows.map(({ key, label, kind, required }) => ({ key, label: label.trim(), kind, required }));
}

/**
 * Why a draft cannot be sent, in one sentence — or null when it can. The
 * platform judges it again; this is the sentence beside the Save button so
 * the refusal is read before the dialog closes.
 */
export function checklistDraftProblem(rows: readonly ChecklistDraftRow[]): string | null {
  if (rows.length > FIELD_CHECKLIST_MAX_STEPS) {
    return `At most ${FIELD_CHECKLIST_MAX_STEPS} steps — a list a technician cannot see the end of is one they stop reading.`;
  }
  const seen = new Set<string>();
  for (const [i, row] of rows.entries()) {
    if (!row.label.trim()) return `Step ${i + 1} needs a label.`;
    if (!FIELD_CHECKLIST_KEY_PATTERN.test(row.key)) {
      return `Step ${i + 1} ("${row.label.trim()}") has no usable key — give it a label with a letter or digit in it.`;
    }
    if (seen.has(row.key)) return `Two steps share the key "${row.key}" — reword one of them.`;
    seen.add(row.key);
  }
  return null;
}

/** "−18.40 dBm" for a light reading, the note for a text step, null for a tick or an undone step. */
export function formatChecklistValue(entry: Pick<FieldChecklistEntry, 'kind' | 'done' | 'value'>): string | null {
  if (!entry.done || entry.value === null) return null;
  if (entry.kind === 'light_reading' && typeof entry.value === 'number') {
    return `${entry.value < 0 ? '−' : ''}${Math.abs(entry.value).toFixed(2)} dBm`;
  }
  return String(entry.value);
}
